/**
 * Candidate widths as `w` descriptors — device pixels, so the top rung is the widest CSS width any
 * consumer renders times the device ratio, not the CSS width itself. `fullBleed` at 1920/dpr2 asks
 * for 3840; stopping at 1400 served that a 2.7x downscale.
 *
 * Spacing is what costs bytes, not length: a rung costs ~28 gzipped bytes, while widening the gaps
 * to six rungs doubles over-fetch (13% -> 30%). 320 is omitted because nothing selects it — a
 * 360px phone at dpr3 needs 1080.
 */
export const CANDIDATE_WIDTHS = [480, 640, 768, 960, 1200, 1400, 1600, 2000, 2400, 3000];

/** Only for browsers that ignore `srcSet`. */
export const FALLBACK_WIDTH = 768;

export type ImageSources = { src: string; srcSet: string };

/**
 * The `src`/`srcSet` pair for one image, or `undefined` when there is no image to render.
 *
 * Takes a URL builder rather than an image id so it stays free of Cloudinary and of React — the
 * caller owns the transformation, this owns the ladder and the descriptor format. Note the URLs
 * must not carry `dpr_`: a `w` descriptor states intrinsic pixels, and the browser applies the
 * device ratio itself, so a baked-in ratio would make every descriptor lie.
 *
 * `toUrl` is called once per rung, and `src` reuses the rung at `FALLBACK_WIDTH` rather than
 * asking for it again.
 */
export const buildImageSources = (toUrl: (width: number) => string | undefined): ImageSources | undefined => {
  const candidates: string[] = [];
  let src: string | undefined;

  for (const width of CANDIDATE_WIDTHS) {
    const url = toUrl(width);
    if (width === FALLBACK_WIDTH) src = url;
    candidates.push(`${url} ${width}w`);
  }

  if (!src) return undefined;

  return { src, srcSet: candidates.join(', ') };
};
